<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import endOfDay from 'date-fns/endOfDay';
import startOfDay from 'date-fns/startOfDay';
import subDays from 'date-fns/subDays';
import getUnixTime from 'date-fns/getUnixTime';
import differenceInCalendarDays from 'date-fns/differenceInCalendarDays';
import fromUnixTime from 'date-fns/fromUnixTime';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { formatTime } from '@chatwoot/utils';
import BarChart from 'shared/components/charts/BarChart.vue';
import { generateFileName } from 'dashboard/helper/downloadHelper';

const { t } = useI18n();
const store = useStore();

const presets = [
  { label: 'Last 3 months', days: 89 },
  { label: 'Last 30 days', days: 29 },
  { label: 'Last 7 days', days: 6 },
];
const activePreset = ref(2);
const showDateMenu = ref(false);
const dateMenuRef = ref(null);
const errorMessage = ref('');
const latestRefreshToken = ref(0);

const now = endOfDay(new Date());
const since = ref(getUnixTime(startOfDay(subDays(now, presets[2].days))));
const until = ref(getUnixTime(now));

const businessHours = ref(false);
const excludeIssueTime = ref(false);
const hideInactive = ref(false);

const sortCol = ref('conversations');
const sortDesc = ref(true);

const teams = useMapGetter('teams/getTeams');
const metrics = useMapGetter('summaryReports/getTeamSummaryReports');
const summary = useMapGetter('getAccountSummary');
const summaryStatus = useMapGetter('getAccountSummaryFetchingStatus');
const uiFlags = useMapGetter('summaryReports/getUIFlags');

const isFetching = computed(() => uiFlags.value?.isFetchingTeamSummaryReports);
const isSummaryLoading = computed(() => summaryStatus.value === 'fetching');

const toFiniteNumber = value => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const pickMetricNumber = (source, keys = [], { fallback = null } = {}) => {
  const found = keys
    .map(key => toFiniteNumber(source?.[key]))
    .find(v => v != null);
  return found ?? fallback;
};

const fmtNum = value =>
  toFiniteNumber(value) == null ? '--' : Number(value).toLocaleString();

const fmtSec = value => {
  const safe = toFiniteNumber(value);
  if (safe == null) return '--';
  const sec = Math.round(Math.abs(safe));
  if (sec < 60) return `${sec}s`;
  const mins = Math.floor(sec / 60);
  const rem = sec % 60;
  return `${mins}min ${rem}s`;
};

const fmtTime = value => {
  const safe = toFiniteNumber(value);
  if (safe == null) return '--';
  return formatTime(safe);
};

const applyPreset = idx => {
  activePreset.value = idx;
  const to = endOfDay(new Date());
  const from = startOfDay(subDays(to, presets[idx].days));
  since.value = getUnixTime(from);
  until.value = getUnixTime(to);
};

const shiftPreset = step => {
  const next = Math.max(
    0,
    Math.min(presets.length - 1, activePreset.value + step)
  );
  if (next !== activePreset.value) applyPreset(next);
};

const onSelectPreset = idx => {
  applyPreset(idx);
  showDateMenu.value = false;
};

const createRefreshToken = () => {
  latestRefreshToken.value += 1;
  return latestRefreshToken.value;
};

const isLatestRefresh = token => token === latestRefreshToken.value;

const fetchData = async () => {
  const refreshToken = createRefreshToken();
  errorMessage.value = '';

  const [teamsResult, teamSummaryResult, accountSummaryResult] =
    await Promise.allSettled([
      store.dispatch('teams/get'),
      store.dispatch('summaryReports/fetchTeamSummaryReports', {
        since: since.value,
        until: until.value,
        businessHours: businessHours.value,
        excludeIssueTime: excludeIssueTime.value,
      }),
      store.dispatch('fetchAccountSummary', {
        from: since.value,
        to: until.value,
        type: 'account',
        groupBy: 'day',
        businessHours: businessHours.value,
        excludeIssueTime: excludeIssueTime.value,
      }),
    ]);

  if (!isLatestRefresh(refreshToken)) return;

  const teamsFailed = teamsResult.status === 'rejected';
  const teamSummaryFailed =
    teamSummaryResult.status === 'rejected' ||
    teamSummaryResult.value === false;
  const accountSummaryFailed =
    accountSummaryResult.status === 'rejected' ||
    accountSummaryResult.value === false;

  if (teamsFailed || teamSummaryFailed || accountSummaryFailed) {
    errorMessage.value =
      'Failed to fetch team performance report. Please try again.';
  }
};

const handleDocumentClick = event => {
  const target = event.target;
  if (
    showDateMenu.value &&
    dateMenuRef.value &&
    !dateMenuRef.value.contains(target)
  ) {
    showDateMenu.value = false;
  }
};

const handleEscape = event => {
  if (event.key !== 'Escape') return;
  showDateMenu.value = false;
};

onMounted(() => {
  fetchData();
  document.addEventListener('click', handleDocumentClick);
  document.addEventListener('keydown', handleEscape);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleDocumentClick);
  document.removeEventListener('keydown', handleEscape);
});

watch([since, until, businessHours, excludeIssueTime], fetchData);

const byId = computed(() => {
  const map = {};
  (metrics.value || []).forEach(item => {
    map[item.id] = item;
  });
  return map;
});

const rows = computed(() => {
  let list = (teams.value || []).map(team => {
    const metric = byId.value[team.id] || {};
    const convs = pickMetricNumber(
      metric,
      ['conversationsCount', 'conversations_count'],
      {
        fallback: 0,
      }
    );
    const prevConvs = pickMetricNumber(metric, [
      'previousConversationsCount',
      'previous_conversations_count',
    ]);
    const frt = pickMetricNumber(metric, [
      'avgFirstResponseTime',
      'avg_first_response_time',
    ]);
    const prevFrt = pickMetricNumber(metric, [
      'previousAvgFirstResponseTime',
      'previous_avg_first_response_time',
    ]);
    const rt = pickMetricNumber(metric, [
      'avgResolutionTime',
      'avg_resolution_time',
    ]);
    const prevRt = pickMetricNumber(metric, [
      'previousAvgResolutionTime',
      'previous_avg_resolution_time',
    ]);
    const reply = pickMetricNumber(metric, [
      'avgReplyTime',
      'avg_reply_time',
      'replyTime',
    ]);
    const prevReply = pickMetricNumber(metric, [
      'previousAvgReplyTime',
      'previous_avg_reply_time',
      'previousReplyTime',
    ]);
    const resCount = pickMetricNumber(metric, [
      'resolvedConversationsCount',
      'resolved_conversations_count',
      'resolutionsCount',
      'resolutions_count',
    ]);
    const prevResCount = pickMetricNumber(metric, [
      'previousResolvedConversationsCount',
      'previous_resolved_conversations_count',
      'previousResolutionsCount',
      'previous_resolutions_count',
    ]);

    return {
      id: team.id,
      name: team.name,
      conversations: convs ?? 0,
      convsDiff:
        prevConvs == null ? null : Number(convs ?? 0) - Number(prevConvs ?? 0),
      firstResp: frt,
      frtDiff:
        prevFrt == null || frt == null ? null : Math.round(frt - prevFrt),
      resolutionTime: rt,
      rtDiff: prevRt == null || rt == null ? null : Math.round(rt - prevRt),
      replyTime: reply,
      replyDiff:
        prevReply == null || reply == null
          ? null
          : Math.round(reply - prevReply),
      resolutionCount: resCount ?? 0,
      resolutionCountDiff:
        prevResCount == null
          ? null
          : Number(resCount ?? 0) - Number(prevResCount ?? 0),
    };
  });

  if (hideInactive.value) {
    list = list.filter(row => Number(row.conversations || 0) > 0);
  }

  const col = sortCol.value;
  list.sort((a, b) => {
    const va = a[col] ?? 0;
    const vb = b[col] ?? 0;
    return sortDesc.value ? vb - va : va - vb;
  });

  return list;
});

const chartRows = computed(() => rows.value.slice(0, 8));
const hasChartData = computed(() => chartRows.value.length > 0);

const chartCollection = computed(() => ({
  labels: chartRows.value.map(row => row.name || '--'),
  datasets: [
    {
      label: 'Total chats',
      data: chartRows.value.map(row => Number(row.conversations || 0)),
      backgroundColor: '#1d75e5',
      borderRadius: 6,
      categoryPercentage: 0.72,
      barPercentage: 0.9,
    },
  ],
}));

const chartOptions = {
  plugins: { legend: { display: false } },
  scales: {
    x: { grid: { drawOnChartArea: false } },
    y: {
      beginAtZero: true,
      ticks: { callback: value => Number(value).toLocaleString() },
      grid: { drawOnChartArea: false },
    },
  },
};

const dateChipCount = computed(() => {
  if (!since.value || !until.value) return 0;
  return Math.max(
    differenceInCalendarDays(
      fromUnixTime(until.value),
      fromUnixTime(since.value)
    ) + 1,
    1
  );
});

const toggleSort = col => {
  if (sortCol.value === col) {
    sortDesc.value = !sortDesc.value;
    return;
  }
  sortCol.value = col;
  sortDesc.value = true;
};

const cellClass = (value, invert = false) => {
  if (value == null || value === 0) return 'cell-neutral';
  const isPositive = invert ? value < 0 : value > 0;
  const abs = Math.abs(value);
  let level = 'lowest';
  if (abs > 50) level = 'high';
  else if (abs > 20) level = 'medium';
  else if (abs > 5) level = 'low';
  return isPositive ? `cell-positive-${level}` : `cell-negative-${level}`;
};

const totalChats = computed(
  () =>
    pickMetricNumber(
      summary.value,
      ['conversations_count', 'conversationsCount'],
      {
        fallback: 0,
      }
    ) ?? 0
);
const prevChats = computed(
  () =>
    pickMetricNumber(
      summary.value?.previous,
      ['conversations_count', 'conversationsCount'],
      {
        fallback: 0,
      }
    ) ?? 0
);
const chatsDiffPct = computed(() => {
  if (!prevChats.value) return 0;
  return Math.round(
    ((totalChats.value - prevChats.value) / prevChats.value) * 100
  );
});

const avgFRT = computed(() =>
  pickMetricNumber(summary.value, [
    'avg_first_response_time',
    'avgFirstResponseTime',
  ])
);
const prevFRT = computed(() =>
  pickMetricNumber(summary.value?.previous, [
    'avg_first_response_time',
    'avgFirstResponseTime',
  ])
);
const frtDiff = computed(() => {
  if (avgFRT.value == null || prevFRT.value == null) return null;
  return Math.round(avgFRT.value - prevFRT.value);
});

const avgResolution = computed(() =>
  pickMetricNumber(summary.value, ['avg_resolution_time', 'avgResolutionTime'])
);
const prevResolution = computed(() =>
  pickMetricNumber(summary.value?.previous, [
    'avg_resolution_time',
    'avgResolutionTime',
  ])
);
const resolutionDiff = computed(() => {
  if (avgResolution.value == null || prevResolution.value == null) return null;
  return Math.round(avgResolution.value - prevResolution.value);
});

const avgReply = computed(() =>
  pickMetricNumber(summary.value, ['reply_time', 'replyTime', 'avgReplyTime'])
);
const prevReply = computed(() =>
  pickMetricNumber(summary.value?.previous, [
    'reply_time',
    'replyTime',
    'avgReplyTime',
  ])
);
const replyDiff = computed(() => {
  if (avgReply.value == null || prevReply.value == null) return null;
  return Math.round(avgReply.value - prevReply.value);
});

const onDownload = () => {
  const fileName = generateFileName({
    type: 'team',
    to: until.value,
    businessHours: businessHours.value,
  });
  store.dispatch('downloadTeamReports', {
    from: since.value,
    to: until.value,
    fileName,
    businessHours: businessHours.value,
    excludeIssueTime: excludeIssueTime.value,
  });
};
</script>

<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<template>
  <div class="tp-page">
    <div class="tp-panel">
      <div class="tp-hdr">
        <div class="tp-hdr-left">
          <h1 class="tp-hdr-title">Team performance</h1>
        </div>
      </div>

      <div class="tp-body">
        <div v-if="errorMessage" class="tp-error">
          {{ errorMessage }}
        </div>

        <div class="tp-filter-row">
          <div class="tp-filter-left">
            <div ref="dateMenuRef" class="tp-date-nav">
              <button
                class="tp-nav-btn"
                type="button"
                :disabled="activePreset === 0"
                @click="shiftPreset(-1)"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  width="16"
                  height="16"
                >
                  <path
                    stroke="currentColor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="m15 6-6 6 6 6"
                  />
                </svg>
              </button>

              <button
                class="tp-date-chip"
                type="button"
                :aria-expanded="showDateMenu ? 'true' : 'false'"
                @click.stop="showDateMenu = !showDateMenu"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  width="16"
                  height="16"
                >
                  <path
                    stroke="currentColor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M4 11h16M8 3v4m8-4v4m-8 8h2v2H8zM4 7a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2z"
                  />
                </svg>
                <strong>Date</strong>
                <span class="tp-date-count">{{ dateChipCount }}</span>
              </button>

              <button
                class="tp-nav-btn"
                type="button"
                :disabled="activePreset === presets.length - 1"
                @click="shiftPreset(1)"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  width="16"
                  height="16"
                >
                  <path
                    stroke="currentColor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="m9 6 6 6-6 6"
                  />
                </svg>
              </button>

              <div v-if="showDateMenu" class="tp-date-menu" @click.stop>
                <div class="tp-date-menu__title">Date filter</div>
                <button
                  v-for="(preset, idx) in presets"
                  :key="preset.label"
                  class="tp-date-menu__item"
                  :class="{
                    'tp-date-menu__item--active': idx === activePreset,
                  }"
                  type="button"
                  @click="onSelectPreset(idx)"
                >
                  <span>{{ preset.label }}</span>
                  <span v-if="idx === activePreset" class="tp-date-menu__check">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      width="14"
                      height="14"
                    >
                      <path
                        stroke="currentColor"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="m5 13 4 4L19 7"
                      />
                    </svg>
                  </span>
                </button>
                <div class="tp-date-menu__footer">
                  <button
                    class="tp-date-menu__done"
                    type="button"
                    @click="showDateMenu = false"
                  >
                    Done
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div class="tp-filter-right">
            <label class="tp-switch">
              <span>Business hours</span>
              <span
                class="tp-switch-track"
                :class="{ 'tp-switch-track--on': businessHours }"
              >
                <input
                  v-model="businessHours"
                  type="checkbox"
                  class="tp-switch-input"
                />
                <span class="tp-switch-thumb" />
              </span>
            </label>
            <label class="tp-switch">
              <span>Exclude issue time</span>
              <span
                class="tp-switch-track"
                :class="{ 'tp-switch-track--on': excludeIssueTime }"
              >
                <input
                  v-model="excludeIssueTime"
                  type="checkbox"
                  class="tp-switch-input"
                />
                <span class="tp-switch-thumb" />
              </span>
            </label>
          </div>
        </div>

        <div class="tp-cards">
          <div class="tp-card">
            <div class="tp-card-label">Total chats</div>
            <div class="tp-card-main">
              <span class="tp-card-value">
                {{ isSummaryLoading ? '—' : totalChats.toLocaleString() }}
              </span>
              <span
                v-if="!isSummaryLoading && (prevChats || chatsDiffPct !== 0)"
                class="tp-pill"
                :class="chatsDiffPct < 0 ? 'tp-pill--red' : 'tp-pill--green'"
              >
                <span>{{ chatsDiffPct < 0 ? '▼' : '▲' }}</span>
                {{ Math.abs(chatsDiffPct) }}%
              </span>
            </div>
          </div>

          <div class="tp-card">
            <div class="tp-card-label">Avg. first response time</div>
            <div class="tp-card-main">
              <span class="tp-card-value">{{
                isSummaryLoading ? '—' : fmtSec(avgFRT)
              }}</span>
              <span
                v-if="!isSummaryLoading && frtDiff != null && frtDiff !== 0"
                class="tp-pill"
                :class="frtDiff > 0 ? 'tp-pill--red' : 'tp-pill--green'"
              >
                <span>{{ frtDiff > 0 ? '▲' : '▼' }}</span>
                {{ fmtSec(frtDiff) }}
              </span>
            </div>
          </div>

          <div class="tp-card">
            <div class="tp-card-label">Avg. resolution time</div>
            <div class="tp-card-main">
              <span class="tp-card-value">
                {{ isSummaryLoading ? '—' : fmtSec(avgResolution) }}
              </span>
              <span
                v-if="
                  !isSummaryLoading &&
                  resolutionDiff != null &&
                  resolutionDiff !== 0
                "
                class="tp-pill"
                :class="resolutionDiff > 0 ? 'tp-pill--red' : 'tp-pill--green'"
              >
                <span>{{ resolutionDiff > 0 ? '▲' : '▼' }}</span>
                {{ fmtSec(resolutionDiff) }}
              </span>
            </div>
          </div>

          <div class="tp-card">
            <div class="tp-card-label">Avg. reply time</div>
            <div class="tp-card-main">
              <span class="tp-card-value">{{
                isSummaryLoading ? '—' : fmtSec(avgReply)
              }}</span>
              <span
                v-if="!isSummaryLoading && replyDiff != null && replyDiff !== 0"
                class="tp-pill"
                :class="replyDiff > 0 ? 'tp-pill--red' : 'tp-pill--green'"
              >
                <span>{{ replyDiff > 0 ? '▲' : '▼' }}</span>
                {{ fmtSec(replyDiff) }}
              </span>
            </div>
          </div>
        </div>

        <div class="tp-detail-header">
          <h2 class="tp-detail-title">Detailed report</h2>
          <div class="tp-detail-actions">
            <label class="tp-switch">
              <span>Hide inactive teams</span>
              <span
                class="tp-switch-track"
                :class="{ 'tp-switch-track--on': hideInactive }"
              >
                <input
                  v-model="hideInactive"
                  type="checkbox"
                  class="tp-switch-input"
                />
                <span class="tp-switch-thumb" />
              </span>
            </label>
            <button
              class="tp-square-btn"
              type="button"
              aria-label="Export CSV"
              @click="onDownload"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="18"
                height="18"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2m-8-1V4m0 12 5-5m-5 5-5-5"
                />
              </svg>
            </button>
          </div>
        </div>

        <div class="tp-table-scroll">
          <table class="tp-tbl">
            <thead>
              <tr>
                <th class="tp-th tp-th--team tp-sticky">Team</th>
                <th
                  class="tp-th tp-th--metric tp-th--sortable"
                  @click="toggleSort('conversations')"
                >
                  <button class="tp-th-btn" type="button">
                    <span class="tp-sort-arrow">{{
                      sortDesc ? '▼' : '▲'
                    }}</span>
                    <span>Total chats</span>
                  </button>
                </th>
                <th class="tp-th tp-th--metric">Avg. first response</th>
                <th class="tp-th tp-th--metric">Avg. resolution</th>
                <th class="tp-th tp-th--metric">Avg. reply</th>
                <th class="tp-th tp-th--metric">Resolution count</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="isFetching">
                <td colspan="6" class="tp-td tp-td--empty">
                  <div class="tp-spinner" />
                </td>
              </tr>
              <tr v-else-if="!rows.length">
                <td colspan="6" class="tp-td tp-td--empty">No teams found.</td>
              </tr>
              <template v-else>
                <tr v-for="row in rows" :key="row.id" class="tp-row">
                  <td class="tp-td tp-td--team tp-sticky">
                    <router-link
                      :to="{
                        name: 'team_reports_show',
                        params: { id: row.id },
                      }"
                      class="tp-team-link"
                    >
                      {{ row.name }}
                    </router-link>
                  </td>

                  <td class="tp-td tp-td--metric">
                    <div
                      class="tp-metric-cell"
                      :class="cellClass(row.convsDiff)"
                    >
                      <div class="tp-cell-value">
                        {{ fmtNum(row.conversations) }}
                      </div>
                      <div
                        v-if="row.convsDiff != null && row.convsDiff !== 0"
                        class="tp-cell-diff"
                        :class="
                          row.convsDiff > 0
                            ? 'tp-cell-diff--pos'
                            : 'tp-cell-diff--neg'
                        "
                      >
                        {{ row.convsDiff > 0 ? '+' : '-'
                        }}{{ fmtNum(Math.abs(row.convsDiff)) }}
                      </div>
                    </div>
                  </td>

                  <td class="tp-td tp-td--metric">
                    <div
                      class="tp-metric-cell"
                      :class="cellClass(row.frtDiff, true)"
                    >
                      <div class="tp-cell-value">
                        {{ fmtTime(row.firstResp) }}
                      </div>
                      <div
                        v-if="row.frtDiff != null && row.frtDiff !== 0"
                        class="tp-cell-diff"
                        :class="
                          row.frtDiff < 0
                            ? 'tp-cell-diff--pos'
                            : 'tp-cell-diff--neg'
                        "
                      >
                        {{ row.frtDiff > 0 ? '+' : '-'
                        }}{{ fmtTime(Math.abs(row.frtDiff)) }}
                      </div>
                    </div>
                  </td>

                  <td class="tp-td tp-td--metric">
                    <div
                      class="tp-metric-cell"
                      :class="cellClass(row.rtDiff, true)"
                    >
                      <div class="tp-cell-value">
                        {{ fmtTime(row.resolutionTime) }}
                      </div>
                      <div
                        v-if="row.rtDiff != null && row.rtDiff !== 0"
                        class="tp-cell-diff"
                        :class="
                          row.rtDiff < 0
                            ? 'tp-cell-diff--pos'
                            : 'tp-cell-diff--neg'
                        "
                      >
                        {{ row.rtDiff > 0 ? '+' : '-'
                        }}{{ fmtTime(Math.abs(row.rtDiff)) }}
                      </div>
                    </div>
                  </td>

                  <td class="tp-td tp-td--metric">
                    <div
                      class="tp-metric-cell"
                      :class="cellClass(row.replyDiff, true)"
                    >
                      <div class="tp-cell-value">
                        {{ fmtTime(row.replyTime) }}
                      </div>
                      <div
                        v-if="row.replyDiff != null && row.replyDiff !== 0"
                        class="tp-cell-diff"
                        :class="
                          row.replyDiff < 0
                            ? 'tp-cell-diff--pos'
                            : 'tp-cell-diff--neg'
                        "
                      >
                        {{ row.replyDiff > 0 ? '+' : '-'
                        }}{{ fmtTime(Math.abs(row.replyDiff)) }}
                      </div>
                    </div>
                  </td>

                  <td class="tp-td tp-td--metric">
                    <div
                      class="tp-metric-cell"
                      :class="cellClass(row.resolutionCountDiff)"
                    >
                      <div class="tp-cell-value">
                        {{ fmtNum(row.resolutionCount) }}
                      </div>
                      <div
                        v-if="
                          row.resolutionCountDiff != null &&
                          row.resolutionCountDiff !== 0
                        "
                        class="tp-cell-diff"
                        :class="
                          row.resolutionCountDiff > 0
                            ? 'tp-cell-diff--pos'
                            : 'tp-cell-diff--neg'
                        "
                      >
                        {{ row.resolutionCountDiff > 0 ? '+' : '-'
                        }}{{ fmtNum(Math.abs(row.resolutionCountDiff)) }}
                      </div>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>

        <section class="tp-chart-panel">
          <div class="tp-chart-head">
            <h2 class="tp-chart-title">Performance trend</h2>
            <p class="tp-chart-subtitle">Top 8 teams by total chats</p>
          </div>
          <div class="tp-chart-body">
            <woot-loading-state
              v-if="isFetching"
              :message="$t('REPORT.LOADING_CHART')"
              class="h-full"
            />
            <div
              v-else-if="!hasChartData"
              class="tp-chart-empty text-sm text-n-slate-11"
            >
              {{ t('LAST_7_DAYS_REPORT.NO_DATA') }}
            </div>
            <BarChart
              v-else
              :collection="chartCollection"
              :chart-options="chartOptions"
            />
          </div>
        </section>
      </div>
    </div>
  </div>
</template>
<!-- eslint-enable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->

<style scoped>
.tp-page,
::global(body.dark) .tp-page {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  width: 100%;
  height: 100%;
  min-height: 100vh;
  min-height: 100dvh;
  background: #ffffff !important;
  color: #111827;
  overflow: auto;
  font-family:
    Inter,
    -apple-system,
    BlinkMacSystemFont,
    'Segoe UI',
    sans-serif;
}

.tp-page * {
  box-sizing: border-box;
}

.tp-panel {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  background: #ffffff;
  border-top: 1px solid #eceff3;
}

.tp-hdr {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  min-height: 58px;
  padding: 0 24px;
  border-bottom: 1px solid #eceff3;
}

.tp-hdr-left {
  display: flex;
  align-items: center;
  gap: 0;
}

.tp-hdr-title {
  margin: 0;
  font-size: 18px !important;
  line-height: 24px;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: #111827;
}

.tp-body {
  flex: 1;
  overflow: auto;
  background: #ffffff;
}

.tp-error {
  margin: 12px 24px 0;
  border: 1px solid #fecaca;
  background: #fef2f2;
  color: #b91c1c;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 13px;
}

.tp-body::after {
  content: '';
  display: block;
  height: 64px;
}

.tp-filter-row {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 24px 8px;
  flex-wrap: wrap;
}

.tp-filter-left {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.tp-filter-right {
  display: inline-flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
}

.tp-date-nav {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  position: relative;
  height: 34px;
  padding: 2px;
  border: 1px solid #d9dde3;
  border-radius: 8px;
  background: #f3f4f6;
}

.tp-nav-btn {
  width: 28px;
  height: 28px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: #4b5563;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.tp-nav-btn:disabled {
  color: #9ca3af;
  cursor: not-allowed;
}

.tp-date-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 28px;
  padding: 0 10px;
  border: 0;
  border-radius: 6px;
  background: #f3f4f6;
  color: #111827;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.tp-date-chip:focus-visible {
  outline: 2px solid #2563eb;
  outline-offset: 1px;
}

.tp-date-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 6px;
  border-radius: 999px;
  background: #e5e7eb;
  color: #111827;
  font-size: 11px;
  font-weight: 700;
}

.tp-date-menu {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  width: 240px;
  padding: 8px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #ffffff;
  box-shadow: 0 20px 28px rgba(17, 24, 39, 0.14);
  z-index: 30;
}

.tp-date-menu__title {
  margin: 0 0 6px;
  padding: 0 4px;
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
}

.tp-date-menu__item {
  width: 100%;
  min-height: 34px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: #111827;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
}

.tp-date-menu__item:hover {
  background: #f3f4f6;
}

.tp-date-menu__item--active {
  background: #eef2ff;
  color: #1d4ed8;
  font-weight: 600;
}

.tp-date-menu__check {
  display: inline-flex;
  align-items: center;
  color: currentcolor;
}

.tp-date-menu__footer {
  margin-top: 8px;
  border-top: 1px solid #eef0f3;
  padding-top: 8px;
  display: flex;
  justify-content: flex-end;
}

.tp-date-menu__done {
  height: 30px;
  border: 1px solid #d9dde3;
  border-radius: 8px;
  background: #ffffff;
  color: #111827;
  font-size: 12px;
  font-weight: 600;
  padding: 0 10px;
  cursor: pointer;
}

.tp-cards {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  padding: 10px 24px 16px;
}

.tp-card {
  display: flex;
  flex-direction: column;
  min-height: 124px;
  padding: 14px 14px 12px;
  border-radius: 10px;
  border: 1px solid #ebedf0;
  background: #f3f4f6;
}

.tp-card-label {
  font-size: 12px;
  line-height: 1.3;
  color: #111827;
  font-weight: 600;
}

.tp-card-main {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  min-height: 56px;
  margin-top: 8px;
}

.tp-card-value {
  font-size: 32px;
  line-height: 1.05;
  font-weight: 700;
  color: #111827;
  letter-spacing: -0.03em;
  white-space: nowrap;
}

.tp-pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 6px;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.2;
  white-space: nowrap;
}

.tp-pill--green {
  color: #157f3f;
  background: #e8f7ed;
}

.tp-pill--red {
  color: #b42318;
  background: #fdebec;
}

.tp-detail-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 24px 8px;
}

.tp-detail-title {
  margin: 0;
  font-size: 16px;
  line-height: 22px;
  font-weight: 600;
  color: #111827;
}

.tp-detail-actions {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.tp-switch {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  user-select: none;
  white-space: nowrap;
}

.tp-switch-input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.tp-switch-track {
  position: relative;
  width: 27px;
  height: 20px;
  border-radius: 15px;
  background: rgb(221, 221, 221);
  cursor: pointer;
  transition: background 0.2s ease;
}

.tp-switch-track--on {
  background: #111827;
}

.tp-switch-thumb {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 15px;
  height: 15px;
  border-radius: 50%;
  background: rgb(255, 255, 255);
  box-shadow:
    rgba(0, 0, 0, 0.05) 0 3px 3px 0,
    rgba(0, 0, 0, 0.1) 0 2px 2px 0,
    rgba(0, 0, 0, 0.05) 0 3px 2px 0;
  transition: left 0.2s ease-in-out;
  will-change: left;
}

.tp-switch-track--on .tp-switch-thumb {
  left: 10px;
}

.tp-square-btn {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  border: 1px solid #d9dde3;
  background: #ffffff;
  color: #374151;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.tp-square-btn:hover {
  background: #f9fafb;
}

.tp-table-scroll {
  overflow-x: auto;
  overflow-y: visible;
  padding: 0 24px 16px;
  scrollbar-width: thin;
  scrollbar-color: #cfd4dc transparent;
  background: #ffffff;
}

.tp-table-scroll::-webkit-scrollbar {
  height: 8px;
}

.tp-table-scroll::-webkit-scrollbar-thumb {
  background: #cfd4dc;
  border-radius: 999px;
}

.tp-tbl {
  width: max-content;
  min-width: 100%;
  border-collapse: collapse;
  border-spacing: 0;
  background: #ffffff;
}

.tp-th {
  padding: 10px 10px;
  text-align: left;
  white-space: nowrap;
  font-size: 11px;
  font-weight: 500;
  color: #111827 !important;
  border-bottom: 1px solid #e5e7eb;
  background: #ffffff !important;
  letter-spacing: 0 !important;
}

.tp-th--team {
  min-width: 260px;
  padding-left: 8px;
}

.tp-th--metric {
  min-width: 150px;
  text-align: center;
}

.tp-th--sortable {
  cursor: pointer;
}

.tp-th-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  cursor: pointer;
}

.tp-sort-arrow {
  font-size: 10px;
  color: #6b7280;
}

.tp-td {
  padding: 8px 10px;
  vertical-align: middle;
  border-bottom: 1px solid #f3f4f6;
  background: #ffffff;
}

.tp-td--team {
  min-width: 260px;
  padding: 0 8px 0 0;
}

.tp-td--metric {
  min-width: 150px;
}

.tp-sticky {
  position: sticky;
  left: 0;
  z-index: 5;
  background: #ffffff !important;
  box-shadow: 8px 0 12px -12px rgba(0, 0, 0, 0.28);
  background-clip: padding-box;
}

.tp-th.tp-sticky {
  z-index: 6;
}

.tp-sticky::before {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  left: -24px;
  width: 24px;
  background: #ffffff;
  pointer-events: none;
}

.tp-team-link {
  display: inline-flex;
  align-items: center;
  min-height: 56px;
  padding-left: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  text-decoration: none;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tp-team-link:hover {
  text-decoration: underline;
}

.tp-metric-cell {
  min-height: 56px;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  padding: 8px 6px;
}

.tp-cell-value {
  font-size: 13px;
  line-height: 1.2;
  font-weight: 600;
  color: #111827;
  text-align: center;
  white-space: nowrap;
}

.tp-cell-diff {
  font-size: 11px;
  line-height: 1.2;
  font-weight: 500;
  text-align: center;
  white-space: nowrap;
}

.tp-cell-diff--pos {
  color: #157f3f;
}

.tp-cell-diff--neg {
  color: #b42318;
}

.cell-positive-high {
  background-color: color-mix(in srgb, #7edd92 90%, transparent);
}

.cell-positive-medium {
  background-color: color-mix(in srgb, #7edd92 70%, transparent);
}

.cell-positive-low {
  background-color: color-mix(in srgb, #7edd92 30%, transparent);
}

.cell-positive-lowest {
  background-color: #f5f5f5;
}

.cell-negative-high {
  background-color: color-mix(in srgb, #ffb7b7 90%, transparent);
}

.cell-negative-medium {
  background-color: color-mix(in srgb, #ffb7b7 70%, transparent);
}

.cell-negative-low {
  background-color: color-mix(in srgb, #ffb7b7 30%, transparent);
}

.cell-negative-lowest {
  background-color: #f5f5f5;
}

.cell-neutral {
  background-color: #f5f5f5;
}

.tp-td--empty {
  text-align: center;
  padding: 32px;
  color: #6b7280;
  background: #ffffff;
}

.tp-spinner {
  width: 30px;
  height: 30px;
  border: 3px solid #e5e7eb;
  border-top-color: #6b7280;
  border-radius: 50%;
  animation: tp-spin 0.8s linear infinite;
  margin: 0 auto;
}

@keyframes tp-spin {
  to {
    transform: rotate(360deg);
  }
}

.tp-chart-panel {
  margin: 30px 24px 10px;
  border: 1px solid #ebedf0;
  border-radius: 10px;
  background: #ffffff;
  overflow: hidden;
}

.tp-chart-head {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 10px;
  padding: 12px 14px 8px;
  border-bottom: 1px solid #f3f4f6;
}

.tp-chart-title {
  margin: 0;
  font-size: 16px;
  line-height: 1.3;
  font-weight: 600;
  color: #111827;
}

.tp-chart-subtitle {
  margin: 0;
  font-size: 12px;
  color: #6b7280;
}

.tp-chart-body {
  height: 280px;
  padding: 10px 14px 14px;
}

.tp-chart-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
}

::global(body.dark) .tp-panel,
::global(body.dark) .tp-hdr,
::global(body.dark) .tp-body,
::global(body.dark) .tp-card,
::global(body.dark) .tp-chart-panel,
::global(body.dark) .tp-tbl,
::global(body.dark) .tp-sticky,
::global(body.dark) .tp-table-scroll,
::global(body.dark) .tp-td,
::global(body.theme-mix) .tp-panel,
::global(body.theme-mix) .tp-hdr,
::global(body.theme-mix) .tp-body,
::global(body.theme-mix) .tp-card,
::global(body.theme-mix) .tp-chart-panel,
::global(body.theme-mix) .tp-tbl,
::global(body.theme-mix) .tp-sticky,
::global(body.theme-mix) .tp-table-scroll,
::global(body.theme-mix) .tp-td {
  background: #ffffff !important;
  border-color: #e5e7eb !important;
  color: #111827 !important;
}

::global(body.dark) .tp-hdr-title,
::global(body.dark) .tp-card-value,
::global(body.dark) .tp-card-label,
::global(body.dark) .tp-detail-title,
::global(body.dark) .tp-cell-value,
::global(body.dark) .tp-team-link,
::global(body.dark) .tp-chart-title,
::global(body.theme-mix) .tp-hdr-title,
::global(body.theme-mix) .tp-card-value,
::global(body.theme-mix) .tp-card-label,
::global(body.theme-mix) .tp-detail-title,
::global(body.theme-mix) .tp-cell-value,
::global(body.theme-mix) .tp-team-link,
::global(body.theme-mix) .tp-chart-title {
  color: #111827 !important;
}

::global(body.dark) .tp-th,
::global(body.dark) .tp-th *,
::global(body.theme-mix) .tp-th,
::global(body.theme-mix) .tp-th * {
  color: #111827 !important;
}

::global(body.dark) .tp-chart-subtitle,
::global(body.dark) .tp-sort-arrow,
::global(body.theme-mix) .tp-chart-subtitle,
::global(body.theme-mix) .tp-sort-arrow {
  color: #6b7280 !important;
}

@media (max-width: 1200px) {
  .tp-cards {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 768px) {
  .tp-cards {
    grid-template-columns: 1fr;
  }
}
</style>
